import { t } from '../i18n';
import { useRef, useState, type ChangeEvent, type FC } from 'react';
import * as api from '../api/client';
import { resetFileTest } from '../api/client';
import type { Lesson } from '../api/client';
import toast from 'react-hot-toast';
import { PdfReader } from './LazyPdfReader';
import { AnalysisVideo } from './AnalysisVideo';
import { ExternalLink, FileText, Paperclip, RotateCcw, X } from 'lucide-react';
const MAX_FILE_BYTES = 20 * 1024 * 1024;

interface FileTestViewerProps {
  lesson: Lesson;
  onSuccess: () => void;
}

const FileTestViewer: FC<FileTestViewerProps> = ({ lesson, onSuccess }) => {
  const [answerText, setAnswerText] = useState(
    lesson.fileTestStudentAnswer || '',
  );
  const [submitting, setSubmitting] = useState(false);
  const [resetting, setResetting] = useState(false);

  const [answerFile, setAnswerFile] = useState<File | null>(null);
  /*
   * Uch holat, ikkitasi emas:
   *   null       — yuklash ketmayapti
   *   'nomalum'  — ketyapti, lekin foizni bilib bo'lmaydi
   *   son        — ketyapti, foiz ma'lum
   *
   * Ilgari faqat son bo'lgan va `e.total` bo'lmaganda u 0 da qolib
   * ketardi — o'quvchi "0%" ni ko'rib "qotib qoldi" deb o'ylardi,
   * holbuki yuklash ketayotgan bo'lishi ham mumkin edi.
   */
  const [uploadPercent, setUploadPercent] = useState<number | 'nomalum' | null>(
    null,
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const maxAttempts = lesson.testMaxAttempts || 0;
  const usedAttempts = lesson.fileTestAttempts || 0;
  /*
   * Ustoz bergan qo'shimcha urinishlar HAM hisobga olinadi.
   *
   * Ilgari bu yerda faqat `maxAttempts - usedAttempts` bo'lgan, server esa
   * `testMaxAttempts + extraAttempts` deb qaragan. Ya'ni ustoz imkoniyat
   * bersa ham o'quvchi "Urinishlar tugagan" ni ko'rardi va "Qayta
   * topshirish" tugmasi chiqmasdi — berilgan imkoniyatdan foydalanib
   * bo'lmasdi. `QuizEngine` da bu to'g'ri, bu yerda esa tushib qolgan edi.
   */
  const extraAttempts = lesson.extraAttempts || 0;
  const effectiveLimit = maxAttempts + extraAttempts;
  const attemptsLeft = maxAttempts > 0 ? effectiveLimit - usedAttempts : null;
  const outOfAttempts = attemptsLeft !== null && attemptsLeft <= 0;
  /** Ustoz qo'shimcha bergan-u, o'quvchi hali ishlatmagan. */
  const hasBonus = extraAttempts > 0 && !outOfAttempts;

  const baseUrl = import.meta.env.VITE_API_URL || window.location.origin;
  const fileUrl = lesson.testFileUrl?.startsWith('/')
    ? `${baseUrl}${lesson.testFileUrl}`
    : lesson.testFileUrl;

  const status = lesson.fileTestStatus || 'PENDING';
  const isPdf = fileUrl?.toLowerCase().endsWith('.pdf');
  const isImage = fileUrl && /\.(jpeg|jpg|gif|png|webp)$/i.test(fileUrl);
  const isAuto = lesson.testCheckMethod === 'AUTO';

  /*
   * Kim tekshiradi va natija qachon keladi.
   *
   * Ilgari bu hech qayerda yozilmagan edi. AI'da javob bir necha
   * soniyada, ustozda esa bir-ikki kunda kelishi mumkin — o'quvchi buni
   * bilmasa, yuborganidan keyin "nega hech narsa bo'lmadi" deb
   * o'ylaydi va qayta-qayta yuborishga urinadi.
   */
  const checkInfo =
    lesson.testCheckMethod === 'MANUAL'
      ? {
          text: t('Ustoz tekshiradi'),
          note: t('Ball qoʻyilgach Telegramga xabar keladi.'),
        }
      : lesson.testCheckMethod === 'AI'
        ? {
            text: t('AI baholaydi'),
            note: t('Natija darhol chiqadi.'),
          }
        : {
            text: t('Avtomat tekshiriladi'),
            note: t('Natija darhol chiqadi.'),
          };

  const openExternally = (url: string) => {
    if (window.Telegram?.WebApp?.openLink) {
      window.Telegram.WebApp.openLink(url);
    } else {
      window.open(url, '_blank');
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (!file) {
      setAnswerFile(null);
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      toast.error(t('Fayl juda katta. Eng koʻpi 20 MB.'));
      e.target.value = '';
      return;
    }
    setAnswerFile(file);
  };

  const handleSubmit = async () => {
    if (isAuto && !answerText.trim()) {
      toast.error(t('Javobingizni kiriting.'));
      return;
    }
    if (!answerText.trim() && !answerFile) {
      toast.error(t('Javob matnini yozing yoki fayl biriktiring.'));
      return;
    }

    setSubmitting(true);
    try {
      let fileUploadUrl: string | undefined;

      if (answerFile) {
        setUploadPercent('nomalum');
        const uploaded = await api.uploadStudentFileApi(answerFile, (p) =>
          setUploadPercent(p === null ? 'nomalum' : p),
        );
        fileUploadUrl = uploaded.url;
      }

      const res = await api.submitFileTest(lesson.id, {
        answerText: answerText.trim() || undefined,
        fileUrl: fileUploadUrl,
      });
      toast.success(res.message || 'Yuborildi.');
      setAnswerFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      onSuccess();
    } catch (e: any) {
      toast.error(api.apiErrorMessage(e, t('Yuborib boʻlmadi.')));
      /*
       * Xatodan keyin ham YANGILAYMIZ.
       *
       * Eng ehtimolli sabab — ekrandagi ma'lumot eskirgani: masalan
       * test allaqachon o'tilgan bo'lsa-yu, ekranda hali javob formasi
       * turgan bo'lsa. Yangilash ekranni haqiqatga keltiradi va
       * o'quvchi bir xil xatoni qayta-qayta ko'rmaydi.
       */
      onSuccess();
    } finally {
      setSubmitting(false);
      setUploadPercent(null);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      const res = await resetFileTest(lesson.id);
      toast.success(res?.message || 'Qayta topshirishga tayyor.');
      onSuccess();
    } catch (e: any) {
      toast.error(api.apiErrorMessage(e, t('Qayta ochib boʻlmadi.')));
      onSuccess(); // yuqoridagi izohga qarang
    } finally {
      setResetting(false);
    }
  };

  return (
    <section className="card">
      <h2
        style={{ fontSize: 'var(--text-lg)', marginBottom: 'var(--space-4)' }}
      >
        {t('Topshiriq')}
      </h2>

      {/* ------------------------------------------------------ savol fayli */}
      {fileUrl ? (
        <div style={{ marginBottom: 'var(--space-5)' }}>
          {isImage ? (
            /*
              Rasm ham, PDF ham BITTA o'qiluvchida. Ilgari rasm oddiy
              <img> edi (kattalashtirib bo'lmasdi), PDF esa pan/zoom
              qatlami ichida turgani uchun umuman scroll bo'lmasdi.
            */
            <PdfReader url={fileUrl} onOpenExternally={openExternally} />
          ) : isPdf ? (
            <PdfReader url={fileUrl} onOpenExternally={openExternally} />
          ) : (
            <div className="empty">
              <FileText size={22} className="muted" aria-hidden="true" />
              <div className="empty-title">{t('Fayl formati nomaʼlum')}</div>
            </div>
          )}

          <button
            type="button"
            className="btn btn-ghost btn-sm"
            style={{ marginTop: 'var(--space-2)' }}
            onClick={() => openExternally(fileUrl)}
          >
            <ExternalLink size={14} aria-hidden="true" />
            {t('Alohida oynada ochish')}
          </button>
        </div>
      ) : (
        <div
          className="callout callout-warning"
          style={{ marginBottom: 'var(--space-5)' }}
        >
          {t('Topshiriq fayli yuklanmagan.')}
        </div>
      )}

      {/* ---------------------------------------------------------- javob */}
      {status === 'PENDING' ? (
        <div className="stack">
          <div className="spread">
            <span className="chip">{checkInfo.text}</span>
            <span className="text-xs muted">{checkInfo.note}</span>
          </div>

          {attemptsLeft !== null && (
            <p className={outOfAttempts ? 'field-error' : 'field-hint'}>
              {outOfAttempts
                ? t('Urinishlar tugagan. Ustozingizga murojaat qiling.')
                : t('Qolgan urinishlar: {left} / {limit}', {
                    left: attemptsLeft,
                    limit: effectiveLimit,
                  })}
            </p>
          )}

          {hasBonus && (
            <div className="callout callout-success">
              <span>
                {t('Ustoz sizga qoʻshimcha imkoniyat berdi: +{count}', {
                  count: extraAttempts,
                })}
              </span>
            </div>
          )}

          <div className="field">
            <label className="field-label" htmlFor="file-test-answer">
              {t('Javobingiz')}
            </label>
            <textarea
              id="file-test-answer"
              className="textarea"
              value={answerText}
              onChange={(e) => setAnswerText(e.target.value)}
              disabled={outOfAttempts}
              placeholder={
                isAuto
                  ? t('Masalan: 1a2b3c')
                  : t('Javobingizni batafsil yozing')
              }
            />
          </div>

          {/* AUTO rejimda javob matn bilan tekshiriladi — fayl kerak emas. */}
          {!isAuto && (
            <div>
              <input
                ref={fileInputRef}
                id="file-test-file"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                onChange={handleFileChange}
                disabled={outOfAttempts || submitting}
                style={{ display: 'none' }}
              />
              <label
                htmlFor="file-test-file"
                className={`dropzone${outOfAttempts || submitting ? ' is-disabled' : ''}`}
              >
                <Paperclip size={16} aria-hidden="true" />
                {answerFile
                  ? answerFile.name
                  : t('Fayl biriktirish — PDF yoki rasm, 20 MB gacha')}
              </label>

              {answerFile && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  style={{ marginTop: 'var(--space-1)' }}
                  onClick={() => {
                    setAnswerFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                >
                  <X size={13} aria-hidden="true" />
                  {t('Faylni olib tashlash')}
                </button>
              )}

              {uploadPercent !== null && (
                <div className="meter" style={{ marginTop: 'var(--space-2)' }}>
                  <div
                    className="meter-fill"
                    style={{
                      // Foiz noma'lum bo'lsa to'liq chiziq — "ketyapti"
                      // degani; 0% esa "to'xtab qolgan" deb o'qiladi.
                      width:
                        uploadPercent === 'nomalum'
                          ? '100%'
                          : `${uploadPercent}%`,
                      opacity: uploadPercent === 'nomalum' ? 0.5 : 1,
                    }}
                  />
                </div>
              )}
            </div>
          )}

          <button
            type="button"
            className="btn btn-primary btn-block"
            onClick={handleSubmit}
            disabled={submitting || outOfAttempts}
          >
            {submitting
              ? typeof uploadPercent === 'number'
                ? t('Yuklanmoqda {percent}%', { percent: uploadPercent })
                : uploadPercent === 'nomalum'
                  ? t('Yuklanmoqda…')
                  : t('Yuborilmoqda…')
              : t('Javobni yuborish')}
          </button>
        </div>
      ) : (
        <div className="stack">
          <div className="card-quiet">
            <span className="stat-label">{t('Yuborilgan javob')}</span>
            {/*
              `overflowWrap: anywhere` SHART.
              Javob kaliti "1a2c3d4a…" ko'rinishida bo'shliqsiz uzun
              satr bo'ladi va brauzer uni bo'linmaydigan bitta so'z deb
              biladi — `pre-wrap` uni ko'chirmaydi, matn katakdan
              chiqib ketadi va o'ng tomoni umuman ko'rinmaydi.
            */}
            <p
              className="prose text-sm"
              style={{
                marginTop: 6,
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
              }}
            >
              {answerText || <i className="muted">{t('Matn yozilmagan')}</i>}
            </p>

            {lesson.fileTestStudentFile && (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                style={{ marginTop: 'var(--space-3)' }}
                onClick={() =>
                  openExternally(
                    lesson.fileTestStudentFile!.startsWith('/')
                      ? `${baseUrl}${lesson.fileTestStudentFile}`
                      : lesson.fileTestStudentFile!,
                  )
                }
              >
                <FileText size={14} aria-hidden="true" />
                {t('Yuborilgan faylni ochish')}
              </button>
            )}
          </div>

          <div
            className={`callout ${status === 'GRADED' ? 'callout-success' : 'callout-warning'}`}
          >
            <span>
              {status === 'CHECKING'
                ? t('Ustoz tekshirmoqda.')
                : t('Baholandi: {score} ball', { score: lesson.score ?? 0 })}
            </span>
          </div>

          {lesson.fileTestAiFeedback && (
            <div className="card-quiet">
              <span className="stat-label">{t('Izoh')}</span>
              <p
                className="prose text-sm"
                style={{ marginTop: 6, overflowWrap: 'anywhere' }}
              >
                {lesson.fileTestAiFeedback}
              </p>
            </div>
          )}

          {/*
            Tahlil videosi — o'tolmagan o'quvchiga.

            Bu FAYLLI topshiriqda UMUMAN ko'rsatilmasdi: `analysisVideoUrl`
            faqat `QuizEngine` da ishlatilardi. Ya'ni ustoz videoni
            yuklardi, o'quvchi esa uni hech qachon ko'rmasdi — xato
            ustida ishlashning yagona yo'li yo'q edi.

            O'tib ketganga ko'rsatilmaydi: unga kerak emas.
          */}
          {status === 'GRADED' &&
            !lesson.isCompleted &&
            (lesson.analysisVideoUrl || lesson.analysisVideoUnavailable) && (
              <div className="card-quiet">
                <span className="stat-label">{t('Xatolar tahlili')}</span>
                <p className="text-xs muted" style={{ marginTop: 4 }}>
                  {t('Ustoz shu darsning yechimini tushuntirgan.')}
                </p>
                <div style={{ marginTop: 'var(--space-3)' }}>
                  <AnalysisVideo
                    url={lesson.analysisVideoUrl}
                    unavailable={lesson.analysisVideoUnavailable}
                  />
                </div>
              </div>
            )}

          {/* Backend o'tilgan testni qayta ochishni rad etadi, shuning uchun
              tugma faqat o'tolmagan va urinishi qolgan holatda ko'rinadi. */}
          {status === 'GRADED' && !lesson.isCompleted && !outOfAttempts && (
            <button
              type="button"
              className="btn btn-secondary btn-block"
              onClick={handleReset}
              disabled={resetting}
            >
              <RotateCcw size={15} aria-hidden="true" />
              {resetting ? t('Ochilmoqda…') : t('Qayta topshirish')}
            </button>
          )}
        </div>
      )}
    </section>
  );
};

export default FileTestViewer;
